import { Button, Stack } from '@mui/material';

/**
 * Single affiliate-link entry rendered by `<AffiliateLinks>`.
 */
export interface AffiliateLink {
  readonly url: string;
  readonly label: string;
}

interface AffiliateLinksProps {
  /**
   * Affiliate links to render. The 10-link cap from Property 17 /
   * R12.3 is enforced by the consumer (game-thunk in task 6.x via
   * `cappedAffiliateLinks(links, 10)`); this component renders the
   * array verbatim and does not slice.
   */
  readonly links: ReadonlyArray<AffiliateLink>;
}

/**
 * `<AffiliateLinks>` — renders a row of MUI `<Button href>` entries,
 * one per supplied affiliate link, with `rel="sponsored noopener
 * noreferrer"` per affiliate-program requirements.
 *
 * **Cap responsibility.** This component is intentionally
 * presentational. The 10-link cap from R12.3 / Property 17 is
 * applied upstream — the consumer pipes its raw link list through
 * `cappedAffiliateLinks(links, 10)` (task 6.3, wired in tasks 6.x)
 * before passing the result here. Keeping the cap out of the
 * renderer means the property-based test for the cap exercises the
 * helper directly without DOM scaffolding.
 *
 * _Requirements: R8.6, R12.3, Property 17, design §6.2._
 */
export function AffiliateLinks({ links }: AffiliateLinksProps): JSX.Element {
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {links.map((link) => (
        <Button
          key={link.url}
          href={link.url}
          variant="outlined"
          rel="sponsored noopener noreferrer"
        >
          {link.label}
        </Button>
      ))}
    </Stack>
  );
}
